import { useEffect, useState } from 'react';
import { useLocation } from '../../shared/services/api/location';
import { css, cx } from '@emotion/css';
import { TabSwitch } from '../../shared/components/TabSwitch';
import InfoGroup from '../../shared/components/InfoGroup';
import { Colors } from '../../shared/styles/colors';
import {
  doctrineStencilStyle,
  h3Style,
  p1Style,
  p2Style,
  p3Style,
  tabularNumbersStyle,
} from '../../shared/styles/text';
import { Link, useParams } from 'react-router-dom';
import { Layout } from '../../shared/components/layout/Layout';
import {
  ChevronRight,
  LinkIcon,
  MailIcon,
  MarexLogo,
  PhoneIcon,
  PowerIcon,
  WaterIcon,
} from '../../shared/assets/icons';
import { Facility, Pedestal } from '../../shared/types/server-types';
import { useNavigator } from '../../routing/navigator';
import { appRoutes } from '../../routing/app-routes';
import { Divider, DividerVariantEnum } from '../../shared/components/Divider';
import { LocationLoadingState } from '../../shared/components/loading-states/LoactionLoadingState';
import { filterSources } from '../../shared/utils/filterSources';
import { http } from '../../shared/services/http';
import { CacheKeys } from '../../shared/services/api/cache-configuration';
import { useUser } from '../../shared/providers/user-provider';
import { useQueryClient } from '@tanstack/react-query';
import {
  Motion,
  Radius,
  Shadow,
  unstyledButton,
} from '../../shared/styles/tokens';
import { BOTTOM_NAV_SAFE_HEIGHT } from '../../shared/styles/css-values';

enum TabSwitchIndex {
  INFO = 0,
  PEDESTALS = 1,
}

export const LocationPage = (): JSX.Element => {
  const { locationId } = useParams<{ locationId: string }>();
  if (!locationId) throw new Error();
  const {
    data: location,
    isLoading,
    isError,
  } = useLocation({ id: locationId });
  const navigator = useNavigator();
  const user = useUser();
  const queryClient = useQueryClient();
  const [creatingAccount, setCreatingAccount] = useState<boolean>(false);

  const [selectedTab, setSelectedTab] = useState<number>(TabSwitchIndex.INFO);

  if (isError) {
    return <p className={errorStyle}>Location could not be loaded.</p>;
  }

  if (isLoading || !location || creatingAccount) {
    return <LocationLoadingState />;
  }

  const {
    imageUrl: locationBackgroundImage,
    logoUrl,
    country,
    city,
    name,
    email,
    websiteUrl,
    phone,
    pedestals,
    constants,
    facilities,
    workingHours,
  } = location;
  const {
    berthCount,
    maxVehicleBeamLength,
    maxVehicleLength,
    portDepthRange,
    maxSocketSize,
    waterSocketSize,
  } = constants;

  const handleOpenPedestal = async (pedestal: Pedestal) => {
    try {
      setCreatingAccount(true);
      if (
        user &&
        user.accounts.some(
          (a) =>
            a.location.id === location.id ||
            a.location.subLocations?.some((sl) => sl.id === location.id),
        )
      ) {
        navigator.toPedestal(pedestal.identifier);
      } else {
        await http.createAccount(location.id);
        await queryClient.refetchQueries({ queryKey: [CacheKeys.ME] });
        navigator.toPedestal(pedestal.identifier);
        setCreatingAccount(false);
      }
    } finally {
      setCreatingAccount(false);
    }
  };

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  // JS: Sunday = 0; the API uses Monday = 1 ... Sunday = 7.
  const todayIndex = (new Date().getDay() + 6) % 7;

  return (
    <>
      <header className={headerStyle(locationBackgroundImage)}>
        <Link
          to={appRoutes.landing}
          className={logoLinkStyle}
          aria-label="Marex home"
        >
          <MarexLogo fill={Colors.WHITE} />
        </Link>
        <div className={locationContactInfoStyle}>
          {websiteUrl && (
            <a
              href={`${websiteUrl}`}
              target="_blank"
              rel="noreferrer"
              aria-label="Website"
            >
              <LinkIcon />
            </a>
          )}
          {phone && (
            <a href={`tel:${phone}`} aria-label={`Call ${phone}`}>
              <PhoneIcon />
            </a>
          )}
          {email && (
            <a href={`mailto:${email}`} aria-label={`Email ${email}`}>
              <MailIcon />
            </a>
          )}
        </div>
      </header>
      <Layout.Content className={locationPageStyle}>
        <div className={locationPageContainerStyle}>
          <figure className={locationNameLocationLogoStyle}>
            <img src={logoUrl} alt="" className={locationLogoStyle} />
            <figcaption>
              <span id={'locationName'}>{name}</span>
              <span id={'locationAddress'}>
                {city}, {country}
              </span>
            </figcaption>
          </figure>
          <div className={locationDescriptionStyle}>{location.description}</div>
          <TabSwitch
            tabs={['info', 'pedestals']}
            selectedIndex={selectedTab}
            setIndex={setSelectedTab}
          />
          {selectedTab === TabSwitchIndex.INFO && (
            <>
              <InfoGroup
                className={infoGroupStyle}
                elements={[
                  {
                    label: 'number of berths',
                    value: berthCount ? `${berthCount}` : '-',
                  },
                  {
                    label: 'max length',
                    value: maxVehicleLength ? `${maxVehicleLength} m` : '-',
                  },
                  {
                    label: 'depth',
                    value: portDepthRange ? `${portDepthRange} m` : '-',
                  },
                  {
                    label: 'max beam',
                    value: maxVehicleBeamLength
                      ? `${maxVehicleBeamLength} m`
                      : '-',
                  },
                  {
                    label: 'max socket size',
                    value: maxSocketSize ? `${maxSocketSize} A` : '-',
                  },
                  {
                    label: 'water socket size',
                    value: waterSocketSize ? `${waterSocketSize}''` : '-',
                  },
                ]}
              />
              {facilities?.length > 0 && (
                <>
                  <Divider variant={DividerVariantEnum.FULL_WIDTH_DIVIDER} />
                  <div className={facilitiesStyle}>
                    <h3 className={cx(h3Style, sectionHeaderStyle)}>
                      Facilities
                    </h3>
                    <ul className={facilitiesListStyle}>
                      {facilities.map((facility: Facility, index) => (
                        <li key={index}>
                          <DynamicIcon iconPath={facility.iconUrl} />
                          {facility.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}

              <Divider variant={DividerVariantEnum.FULL_WIDTH_DIVIDER} />
              <div className={locationMapStyle}>
                <h3 className={cx(h3Style, sectionHeaderStyle)}>Location</h3>
                <div className={locationAddressStyle}>
                  <span id={'location-name'}>{location.name}</span>
                  <span>{location.address}</span>
                  <span>{`${location.postcode} ${location.city}`}</span>
                  <span>{location.country}</span>
                </div>
                <iframe
                  title={`Map of ${location.name}`}
                  src={`https://maps.google.com/maps?q=${location.latitude},${location.longitude}&hl=en&z=14&amp&output=embed`}
                  allowFullScreen={true}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className={embeddedMapStyle}
                ></iframe>
              </div>
            </>
          )}
          {workingHours && selectedTab === TabSwitchIndex.INFO && (
            <>
              <Divider variant={DividerVariantEnum.FULL_WIDTH_DIVIDER} />
              <div className={openingHoursStyle}>
                <h3 className={cx(h3Style, sectionHeaderStyle)}>Open hours</h3>
                <ul className={openingHoursListStyle}>
                  {dayNames.map((dayName: string, index) => {
                    const entry = workingHours.find(
                      (wh) => wh.dayOfTheWeek === index + 1,
                    );
                    return (
                      <li
                        key={index}
                        className={cx(index === todayIndex && todayStyle)}
                        aria-current={index === todayIndex ? 'date' : undefined}
                      >
                        <span id={'day-range'}>{dayName}</span>
                        <span id={'time-range'}>
                          {String(entry?.openingTime ?? '')}
                          {' – '}
                          {String(entry?.closingTime ?? '')}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </>
          )}
          {workingHours && selectedTab === TabSwitchIndex.PEDESTALS && null}
          {selectedTab === TabSwitchIndex.PEDESTALS && (
            <ul className={pedestalListStyle}>
              {pedestals.map((item, index) => {
                const { availablePower, availableWater } = filterSources(
                  item.valves,
                  item.sockets,
                );
                const { sockets, valves, identifier } = item;
                return (
                  <li key={index}>
                    <button
                      type="button"
                      onClick={() => handleOpenPedestal(item)}
                      className={pedestalCardStyle}
                    >
                      <div className={pedestalIdStyle}>
                        {identifier.toUpperCase()}
                      </div>
                      <div className={pedestalAvailabilityInfoStyle}>
                        <figure>
                          <PowerIcon />
                          <figcaption className={p2Style}>
                            {availablePower}/{sockets.length}
                          </figcaption>
                        </figure>
                        <figure>
                          <WaterIcon />
                          <figcaption className={p2Style}>
                            {availableWater}/{valves.length}
                          </figcaption>
                        </figure>
                      </div>
                      <ChevronRight className={pedestalChevronStyle} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </Layout.Content>
    </>
  );
};

const locationPageStyle = css`
  display: flex;
  flex-direction: column;
  padding-top: 0px;
`;

const locationPageContainerStyle = css`
  display: flex;
  flex-direction: column;
`;

const errorStyle = css`
  padding: 2rem 1.5rem;
  color: ${Colors.TEXT_SECONDARY};
`;

const headerStyle = (locationBackgroundImage: string) => css`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: calc(1.25rem + env(safe-area-inset-top, 0px)) 1.5rem 2.5rem;
  width: 100%;
  height: 17.5625rem;
  background-color: ${Colors.CAPRI_BLUE};
  background-image:
    linear-gradient(
      180deg,
      rgba(29, 88, 131, 0.9) 0%,
      rgba(29, 88, 131, 0.1) 55%,
      rgba(18, 58, 87, 0.55) 100%
    ),
    url(${locationBackgroundImage});
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
`;

const logoLinkStyle = css`
  display: inline-flex;
  align-self: flex-start;
  border-radius: ${Radius.sm};
`;

const locationContactInfoStyle = css`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  > a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.75rem;
    height: 2.75rem;
    background: ${Colors.WHITE};
    border-radius: 50%;
    box-shadow: ${Shadow.card};
    cursor: pointer;
    transition:
      transform ${Motion.fast},
      background-color ${Motion.fast};

    @media (hover: hover) {
      &:hover {
        background: ${Colors.CAPRI_BLUE_TINT};
      }
    }
    &:active {
      transform: scale(0.94);
    }
  }
`;

const locationNameLocationLogoStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.25rem;
  > img {
    margin-top: -2.5rem;
  }
  > figcaption {
    display: flex;
    flex-direction: column;
    min-width: 0;
    padding-top: 0.5rem;
    #locationName {
      width: 100%;
      ${doctrineStencilStyle}
      font-size: 24px;
      font-style: normal;
      font-weight: 400;
      line-height: 29px;
      letter-spacing: 0px;
      text-align: left;
      overflow-wrap: anywhere;
    }
    #locationAddress {
      ${p3Style}
      color: ${Colors.TEXT_SECONDARY};
      text-transform: capitalize;
    }
  }
`;

const locationLogoStyle = css`
  width: 4.5rem;
  height: 4.5rem;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid ${Colors.WHITE};
  background: ${Colors.WHITE};
  box-shadow: ${Shadow.card};
  flex: 0 0 auto;
`;

const locationDescriptionStyle = css`
  ${p1Style}
  margin-bottom: 1.5rem;
`;

const infoGroupStyle = css`
  margin: 1.25rem 0 1rem;
`;

const facilitiesStyle = css`
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
`;

const sectionHeaderStyle = css`
  margin-bottom: 1rem;
`;

const facilitiesListStyle = css`
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  > li {
    ${p2Style}
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.875rem 0.5rem 0.75rem;
    background: ${Colors.SURFACE};
    border: 1px solid ${Colors.BORDER};
    border-radius: ${Radius.pill};
    line-height: 1.25rem;
  }
`;

const openingHoursStyle = css`
  margin-top: 1.5rem;
`;

const openingHoursListStyle = css`
  list-style: none;
  display: flex;
  flex-direction: column;
  margin-bottom: calc(${BOTTOM_NAV_SAFE_HEIGHT} + 2rem);
  border: 1px solid ${Colors.BORDER};
  border-radius: ${Radius.md};
  overflow: hidden;
  > li {
    ${p2Style}
    ${tabularNumbersStyle}
    display: flex;
    justify-content: space-between;
    padding: 0.625rem 1rem;
    #time-range {
      color: ${Colors.CAPRI_BLUE};
    }
  }
  > li + li {
    border-top: 1px solid ${Colors.BORDER};
  }
`;

const todayStyle = css`
  background: ${Colors.CAPRI_BLUE_TINT};
  font-weight: bold;
`;

const locationMapStyle = css`
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
`;

const locationAddressStyle = css`
  ${p2Style}
  display: flex;
  flex-direction: column;
  #location-name {
    color: ${Colors.CAPRI_BLUE};
    font-weight: bold;
  }
`;

const embeddedMapStyle = css`
  margin-top: 1.25rem;
  width: 100%;
  height: 13rem;
  border: 1px solid ${Colors.BORDER};
  border-radius: ${Radius.lg};
  background: ${Colors.SURFACE};
`;

const pedestalListStyle = css`
  list-style: none;
  margin-top: 1.25rem;
  margin-bottom: calc(${BOTTOM_NAV_SAFE_HEIGHT} + 2rem);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const pedestalCardStyle = css`
  ${unstyledButton}
  width: 100%;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.875rem 0.875rem 0.875rem 1rem;
  border: 1px solid ${Colors.BORDER};
  border-radius: ${Radius.md};
  background: ${Colors.WHITE};
  transition:
    background-color ${Motion.fast},
    border-color ${Motion.fast},
    transform ${Motion.fast};

  @media (hover: hover) {
    &:hover {
      background: ${Colors.SURFACE};
      border-color: ${Colors.BORDER_STRONG};
    }
  }
  &:active {
    transform: scale(0.99);
  }
`;

const pedestalIdStyle = css`
  ${doctrineStencilStyle}
  font-size: 1.375rem;
  color: ${Colors.INK};
  flex: 1;
  text-align: left;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const pedestalAvailabilityInfoStyle = css`
  ${p2Style}
  ${tabularNumbersStyle}
  display: flex;
  gap: 0.75rem;
  figure {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    svg {
      width: 1.25rem;
      height: 1.25rem;
    }
    figcaption {
      min-width: 2rem;
      text-align: left;
      color: ${Colors.TEXT_SECONDARY};
    }
  }
`;

const pedestalChevronStyle = css`
  flex: 0 0 auto;
  path {
    stroke: ${Colors.WINDOW_GRAY};
  }
`;

interface DynamicIconProps {
  iconPath: string;
}

const facilityIcons = import.meta.glob<{ default: string }>(
  '../../assets/**/*.svg',
  { eager: false, query: '?url', import: 'default' },
);

// eslint-disable-next-line react/prop-types
const DynamicIcon: React.FC<DynamicIconProps> = ({ iconPath }) => {
  const [icon, setIcon] = useState<string | null>(null);

  useEffect(() => {
    const importIcon = async () => {
      try {
        const key = `../../assets${iconPath}`;
        const loader = facilityIcons[key];
        if (loader) {
          const url = (await loader()) as unknown as string;
          setIcon(url);
        }
      } catch (error) {
        console.error(error);
      }
    };

    importIcon();
  }, [iconPath]);

  return (
    <span className={facilityIconStyle} aria-hidden="true">
      {icon && <img src={icon} width="16" height="20" alt="" />}
    </span>
  );
};

const facilityIconStyle = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  flex: 0 0 auto;

  img {
    width: auto;
    height: 1.125rem;
  }
`;
